<template>
  <v-layout v-if="!$apollo.loading" column class="wrapper">
    <v-flex class="title-screen" xs12>
      <span>Floor plan overview</span>
    </v-flex>
    <v-select
      v-if="eventTemplateId"
      v-model="selectedEventId"
      :items="performanceDates"
      label="Performance"
      item-text="from"
      item-value="id"
      box
      :disabled="updatingFloorPlan"
      style="max-width: 1000px"
    />
    <spinner v-if="!showFloorPlan" />
    <div v-else-if="!venue">This event does not have a floor plan.</div>
    <div v-else class="floor-plan-overview-wrapper">
      <div :class="['floor-plan-wrapper', updatingFloorPlan && 'bkg-color-blue']">
        <div class="card-title">Floor plan</div>
        <div :class="['controls-wrapper', updatingFloorPlan && 'bkg-color-blue']">
          <div
            class="manage-floor-plan"
            :style="`${
              updatingFloorPlan && 'box-shadow: 0px 4px 16px 0px rgba(51, 51, 51, 0.16);'
            }`"
            @click="onManageFloorPlanClick"
          >
            <img src="/assets/manage-floor-plan.svg" alt="Manage floor plan" />
            <span>{{ updatingFloorPlan ? 'Save floor plan' : 'Manage floor plan' }}</span>
          </div>

          <div class="controls-buttons">
            <v-icon
              :size="43"
              :color="zoomLevel > 2.5 ? '#D9D9D9' : '#0277BD'"
              style="user-select: none"
              @click="zoomIn"
            >
              add_circle
            </v-icon>
            <v-icon
              :size="43"
              :color="zoomLevel < 0.2 ? '#D9D9D9' : '#0277BD'"
              style="user-select: none"
              @click="zoomOut"
            >
              remove_circle
            </v-icon>
          </div>
        </div>
        <div
          v-if="updatingFloorPlan"
          :class="['managing-controls', updatingFloorPlan && 'bkg-color-blue']"
        >
          <div class="reset-all-button" @click="onResetAllClick">
            <img src="/assets/reset-icon.svg" alt="reset icon" />
            <span class="reset-all-text">RESET ALL</span>
          </div>
          <ticket-chip
            v-if="selectedSeatIds.length"
            :label="`Set ${findOppositeSelectedSeatsType()}`"
            :number="selectedSeatIds.length"
            @click="onChangeSeatStatus"
          >
            <div slot="left-slot" :class="`circle selected-circle-${findSelectedSeatsType()}`" />
            <div slot="right-slot" class="link-icon">
              <img src="/assets/link-icon.svg" alt="link icon" />
            </div>
          </ticket-chip>
        </div>
        <div :class="['floor-plan-container', updatingFloorPlan && 'bkg-color-blue']">
          <div ref="objectContainerRef" class="object-container">
            <spinner v-if="coloringInProgress" />
            <object
              v-show="!coloringInProgress"
              id="svgfile"
              :data="venue.imageUrl"
              type="image/svg+xml"
              style="width: 100%"
              @load="changeSeatColors"
            />
            <span
              v-if="seatInfoVisible"
              class="tooltip"
              :style="{ bottom: seatInfoPosition.bottom, left: seatInfoPosition.left }"
            >
              R: {{ extractString(hoveredSeat).row }} S: {{ extractString(hoveredSeat).seat }}
              <span v-if="hoveredSeatHolderName"><br />{{ hoveredSeatHolderName }}</span>
            </span>
          </div>
        </div>
      </div>
      <div class="tickets-container">
        <div class="card-title">Tickets</div>
        <div class="available-stats-container">
          <span class="base-text">Free </span>
          <span class="base-text bold-text">
            {{ availableSeats.length }}/{{ audienceCapacity }}
          </span>
        </div>

        <div class="ticket-types-container">
          <ticket-chip
            v-for="orderItem in orderItems"
            :label="orderItem.name"
            :number="orderItem.ticketsNumber"
            :selected="orderItem.selected"
            @click="handleOrderItemClick(orderItem)"
          >
            <div slot="left-slot" class="circle" :style="{ backgroundColor: orderItem.color }" />
            <div slot="right-slot" class="price-amount">{{ orderItem.price.toFixed(2) }}€</div>
          </ticket-chip>
        </div>

        <div class="additional-seat-info-container">
          <div class="additional-seat-info-title">Additional seat info</div>
          <div class="additional-items">
            <ticket-chip
              label="Sold"
              :number="
                audienceCapacity -
                availableSeats.length -
                disabledSeats.length -
                reservedTicketsLength
              "
            >
              <span slot="left-slot" class="circle sold-circle" />
            </ticket-chip>
            <ticket-chip label="Booked" :number="reservedTicketsLength">
              <span slot="left-slot" class="circle booked-circle">R</span>
            </ticket-chip>
            <ticket-chip label="Available" :number="availableSeats.length">
              <span slot="left-slot" class="circle available-circle" />
            </ticket-chip>
            <ticket-chip label="Unavailable" :number="disabledSeats.length">
              <span slot="left-slot" class="circle unavailable-circle" />
            </ticket-chip>
          </div>
        </div>
      </div>
    </div>
    <simple-snackbar v-bind.sync="snackbar" />
  </v-layout>
</template>

<script>
import Spinner from '../shared/spinner.vue'
import {
  GET_EVENTS_BY_EVENT_TEMPLATE_ID,
  GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
} from '@/graphql/event.graphql'
import moment from 'moment/moment'
import TicketChip from '@/components/shared/ticket-chip.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { difference, uniqBy } from 'lodash'
import { updateVenueAvailability } from '@/http'
import { SeatState } from '@/types'
import sumBy from 'lodash/sumBy'

export default {
  name: 'theater-floor-plan-overview',
  components: {
    SimpleSnackbar,
    TicketChip,
    Spinner,
  },
  props: {
    eventId: String,
    eventTemplateId: String,
    organizationSlug: String,
  },
  data() {
    return {
      selectedEventId: null,
      performanceDates: [],
      showFloorPlan: false,
      venue: null,
      orderItems: [],
      availableSeats: [],
      initialAvailableSeats: [],
      disabledSeats: [],
      initialDisabledSeats: [],
      zoomLevel: 1,
      lastPinchDistance: null,
      coloringInProgress: true,
      audienceCapacity: 0,
      updatingFloorPlan: false,
      selectedSeatIds: [],
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      snackbar: {},
      seatInfoVisible: false,
      hoveredSeat: null,
      seatInfoPosition: null,
      reservedTicketsLength: 0,
      reservedTicketsHolderNames: [],
      hoveredSeatHolderName: null,
    }
  },
  watch: {
    async selectedEventId(val) {
      if (!val) return
      await this.fetchFloorPlanAvailabilityData()
    },
  },
  created() {
    if (this.eventTemplateId) {
      this.fetchPerformances()
    } else {
      this.selectedEventId = this.eventId
    }
  },
  methods: {
    changeSeatColors() {
      this.addHoverSeatEventListeners()
      const svgDoc = document.getElementById('svgfile').contentDocument

      for (const availableSeatId of this.availableSeats) {
        const circle = svgDoc.getElementById(availableSeatId)

        if (circle) {
          circle.style.fill = '#979797'
        }
      }

      for (const orderItem of this.orderItems) {
        const soldSeatsIds = orderItem.soldSeats

        for (const seatId of soldSeatsIds) {
          const circle = svgDoc.getElementById(seatId)

          if (circle) {
            circle.style.stroke = orderItem.selected ? orderItem.color : '#a1a0a0'

            circle.style.fill = orderItem.selected ? orderItem.color : '#F5F5F5'
          }
        }
        const reservedSeatsIds = orderItem.reservedSeats

        for (const seatId of reservedSeatsIds) {
          const circle = svgDoc.getElementById(seatId)

          if (!circle) {
            continue
          }

          circle.style.stroke = orderItem.selected ? orderItem.color : '#979797'

          circle.style.fill = orderItem.selected ? orderItem.color : '#D9D9D9'

          if (orderItem.selected) {
            continue
          }

          // add the letter 'R' to reserved seats
          const cx = circle.getAttribute('cx')
          const cy = circle.getAttribute('cy')

          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
          text.setAttribute('x', cx)
          text.setAttribute('y', cy)
          text.setAttribute('text-anchor', 'middle')
          text.setAttribute('dominant-baseline', 'middle')
          text.setAttribute('font-size', '10')
          text.setAttribute('fill', '#333333')
          text.textContent = 'R'
          text.classList.add(`reserved-seat-text-${orderItem.id}`)
          text.style.cursor = 'default'
          text.style.pointerEvents = 'none'
          text.setAttribute('font-family', "'Source Sans Pro', Arial, sans-serif")

          circle.parentNode.insertBefore(text, circle.nextSibling)
        }
      }

      for (const disabledSeatId of this.disabledSeats) {
        const circle = svgDoc.getElementById(disabledSeatId)

        if (circle) {
          circle.style.fill = '#D9D9D9'
        }
      }

      this.coloringInProgress = false

      const floorPlanContainer = document.querySelector('.floor-plan-container')
      floorPlanContainer.addEventListener('touchstart', this.handleTouchStart, { passive: false })
      floorPlanContainer.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      floorPlanContainer.addEventListener('touchend', this.handleTouchEnd)
    },
    handleOrderItemClick(orderItem) {
      orderItem.selected = !orderItem.selected
      if (orderItem.selected) {
        const reservedSeatsText = document
          .querySelector('#svgfile')
          .contentDocument.getElementsByClassName(`reserved-seat-text-${orderItem.id}`)
        Array.from(reservedSeatsText).forEach(el => el.remove())
      }
      this.changeSeatColors()
    },
    zoomIn() {
      if (this.zoomLevel > 2.5) return

      this.zoomLevel += 0.1
      this.applyZoom()
    },
    zoomOut() {
      if (this.zoomLevel < 0.2) return

      this.zoomLevel -= 0.1
      this.applyZoom()
    },

    applyZoom() {
      const svg = document.getElementById('svgfile')
      svg.style.transform = `scale(${this.zoomLevel})`
      svg.style.transformOrigin = 'top left'
    },
    handleTouchStart(event) {
      event.preventDefault()

      if (event.touches.length >= 2) {
        this.lastPinchDistance = Math.hypot(
          event.touches[0].pageX - event.touches[1].pageX,
          event.touches[0].pageY - event.touches[1].pageY,
        )
      }
    },
    handleTouchMove(event) {
      event.preventDefault()

      if (this.lastPinchDistance !== null && event.touches.length >= 2) {
        const newPinchDistance = Math.hypot(
          event.touches[0].pageX - event.touches[1].pageX,
          event.touches[0].pageY - event.touches[1].pageY,
        )

        const delta = newPinchDistance - this.lastPinchDistance
        const sensitivity = 0.01

        if (delta > 0) {
          this.zoomIn(sensitivity * delta)
        } else if (delta < 0) {
          this.zoomOut(-sensitivity * delta)
        }

        this.lastPinchDistance = newPinchDistance
      }
    },

    handleTouchEnd() {
      this.lastPinchDistance = null
    },

    async fetchPerformances() {
      const {
        data: { getEventsByEventTemplateId },
      } = await this.$apollo.query({
        query: GET_EVENTS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })

      this.performanceDates = getEventsByEventTemplateId.map(p => {
        return { ...p, from: moment(p.from).format('DD.MM.YYYY u HH:mm') }
      })
      this.selectedEventId = this.performanceDates[0].id || ''
    },

    checkIfSeatIsSold(seatId) {
      return !this.disabledSeats.includes(seatId) && !this.availableSeats.includes(seatId)
    },

    seatClickEventListener(e) {
      const seatCheck = ['Seat-', 'Row-']
      const svgDoc = document.getElementById('svgfile').getSVGDocument()

      const elementId = e.target.id
      if (
        !seatCheck.every(check => elementId.includes(check)) ||
        this.checkIfSeatIsSold(elementId)
      ) {
        return
      }

      const clickedSeatId = e.target.id

      if (!this.checkIfSeatHasTheSameType(clickedSeatId)) {
        this.snackbar = {
          open: true,
          messageProp: 'Only seats with the same status can be selected.',
          color: 'error',
        }

        return
      }

      if (this.selectedSeatIds.includes(clickedSeatId)) {
        this.selectedSeatIds = this.selectedSeatIds.filter(seatId => seatId !== clickedSeatId)
        svgDoc.getElementById(clickedSeatId).removeAttribute('stroke')
      } else {
        this.selectedSeatIds.push(clickedSeatId)
        svgDoc.getElementById(clickedSeatId).setAttribute('stroke', '#333333')
        svgDoc.getElementById(clickedSeatId).setAttribute('stroke-width', 3.2)
      }
    },

    addSeatClickListeners() {
      document
        .querySelector('#svgfile')
        .contentDocument.addEventListener(
          this.isMobile ? 'touchend' : 'mouseup',
          this.seatClickEventListener,
        )
    },

    removeSeatClickListeners() {
      document
        .querySelector('#svgfile')
        .contentDocument.removeEventListener(
          this.isMobile ? 'touchend' : 'mouseup',
          this.seatClickEventListener,
        )
    },

    async onManageFloorPlanClick() {
      this.updatingFloorPlan = !this.updatingFloorPlan
      if (this.updatingFloorPlan) {
        this.addSeatClickListeners()

        return
      }

      this.unselectSeats()
      this.removeSeatClickListeners()

      const changedDisabledSeats = difference(this.disabledSeats, this.initialDisabledSeats)
      const changedAvailableSeats = difference(this.availableSeats, this.initialAvailableSeats)

      if (changedDisabledSeats.length) {
        const { errors } = await updateVenueAvailability({
          eventId: this.selectedEventId,
          seats: changedDisabledSeats,
          organizationSlug: this.organizationSlug,
          state: 'DISABLED',
          venueId: this.venue.id,
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
        }

        await this.fetchFloorPlanAvailabilityData()
      }

      if (changedAvailableSeats.length) {
        const { errors } = await updateVenueAvailability({
          eventId: this.selectedEventId,
          seats: changedAvailableSeats,
          organizationSlug: this.organizationSlug,
          state: SeatState.AVAILABLE,
          venueId: this.venue.id,
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
        }

        await this.fetchFloorPlanAvailabilityData()
      }
    },

    findSelectedSeatsType() {
      return `${this.disabledSeats.includes(this.selectedSeatIds[0]) ? 'un' : ''}available`
    },

    findOppositeSelectedSeatsType() {
      return this.findSelectedSeatsType() === 'available' ? 'unavailable' : 'available'
    },

    checkIfSeatHasTheSameType(seatId) {
      if (!this.selectedSeatIds.length) return true

      return (
        [this.selectedSeatIds[0], seatId].every(id => this.disabledSeats.includes(id)) ||
        [this.selectedSeatIds[0], seatId].every(id => !this.disabledSeats.includes(id))
      )
    },

    onChangeSeatStatus() {
      const svgDoc = document.getElementById('svgfile').getSVGDocument()

      for (const seatId of this.selectedSeatIds) {
        svgDoc.getElementById(seatId).removeAttribute('stroke')
        if (this.findOppositeSelectedSeatsType() === 'unavailable') {
          svgDoc.getElementById(seatId).style.fill = '#D9D9D9'
          this.availableSeats = this.availableSeats.filter(id => id !== seatId)
          this.disabledSeats.push(seatId)
        } else {
          svgDoc.getElementById(seatId).style.fill = '#979797'
          this.disabledSeats = this.disabledSeats.filter(id => id !== seatId)
          this.availableSeats.push(seatId)
        }
        this.selectedSeatIds = this.selectedSeatIds.filter(id => id !== seatId)
      }
    },

    async fetchFloorPlanAvailabilityData() {
      this.showFloorPlan = false

      const { data } = await this.$apollo.query({
        query: GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
        variables: { eventId: this.selectedEventId },
      })
      if (!data.getFloorPlanAvailabilityOverviewData) {
        return (this.showFloorPlan = true)
      }
      const {
        venue,
        soldOrderItems,
        reservedOrderItems,
        availableSeats,
        disabledSeats,
        audienceCapacity,
      } = data.getFloorPlanAvailabilityOverviewData

      this.venue = venue
      this.reservedTicketsHolderNames = []
      this.orderItems = soldOrderItems.map(item => {
        let reservedSeats = []
        const sameOrderItem = reservedOrderItems.find(reservedItem => reservedItem.id === item.id)
        if (sameOrderItem) {
          reservedSeats = sameOrderItem.seats
          item.ticketsNumber += sameOrderItem.ticketsNumber
          this.reservedTicketsHolderNames.push(...sameOrderItem.seatHolders)
        }

        return {
          ...item,
          soldSeats: item.seats,
          reservedSeats,
          selected: false,
        }
      })
      this.reservedTicketsLength = sumBy(reservedOrderItems, item => item.ticketsNumber)
      this.availableSeats = availableSeats
      this.initialAvailableSeats = [...availableSeats]
      this.disabledSeats = disabledSeats
      this.initialDisabledSeats = [...disabledSeats]
      this.audienceCapacity = audienceCapacity + disabledSeats.length

      this.coloringInProgress = false
      this.showFloorPlan = true
    },

    onResetAllClick() {
      this.unselectSeats()
      this.disabledSeats = [...this.initialDisabledSeats]
      this.availableSeats = [...this.initialAvailableSeats]
      this.changeSeatColors()
    },

    addHoverSeatEventListeners() {
      document
        .querySelector('#svgfile')
        .contentDocument.addEventListener(this.isMobile ? '' : 'mouseover', e => {
          if (e.target.id.includes('Row-')) {
            this.showSeatInfo(e.target.id, e)
          } else {
            this.hideSeatInfo()
          }
        })
    },

    showSeatInfo(seat, event) {
      this.hoveredSeat = seat
      this.seatInfoVisible = true

      const reservedSeatHolder = this.reservedTicketsHolderNames.find(
        item => item.ticketLocation === seat,
      )
      if (reservedSeatHolder) {
        this.hoveredSeatHolderName = reservedSeatHolder.name
      } else {
        this.hoveredSeatHolderName = null
      }

      this.seatInfoPosition = {
        bottom:
          this.$refs.objectContainerRef.offsetHeight - event.clientY * this.zoomLevel + 18 + 'px',
        left: event.clientX * this.zoomLevel - 40 + 'px',
      }
    },

    hideSeatInfo() {
      this.seatInfoVisible = false
      this.hoveredSeatHolderName = null
    },

    extractString(inputString) {
      const match = inputString.match(/^Section-([^_]+)_Row-([^_]+)_Seat-([^_]+)$/)

      if (!match) {
        return { section: '', row: '', seat: '' }
      }

      const [, section, row, seat] = match

      return { section, row, seat }
    },

    unselectSeats() {
      for (const seatId of this.selectedSeatIds) {
        document
          .getElementById('svgfile')
          .getSVGDocument()
          .getElementById(seatId)
          .removeAttribute('stroke')
      }
      this.selectedSeatIds = []
    },
  },
}
</script>

<style scoped>
.wrapper {
  max-width: 1033px;
}

@media screen and (max-width: 800px) {
  .wrapper {
    margin-top: 1.5rem;
  }
}

.floor-plan-wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  border: 1px solid #d9d9d9;
  overflow: hidden;
}

.floor-plan-container {
  padding: 3rem;
  position: relative;
  overflow: auto;
  width: 100%;
}

.object-container {
  overflow: auto;
  position: relative;

  -ms-overflow-style: none;
  scrollbar-width: none;
  background-color: white;
}

.object-container::-webkit-scrollbar {
  display: none;
}

.floor-plan-overview-wrapper {
  display: flex;
  gap: 1.4375rem;
}

@media screen and (max-width: 1200px) {
  .floor-plan-overview-wrapper {
    flex-direction: column;
  }

  .floor-plan-overview-wrapper .tickets-container {
    width: 100%;
  }
}

.card-title {
  display: flex;
  padding: 1rem 1.5rem;
  align-items: center;
  gap: 0.4375rem;
  background-color: #f5f5f5;
  width: 100%;

  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
}

.controls-wrapper {
  display: flex;
  padding: 1rem 1.5rem;
  align-items: center;
  gap: 0.5rem;
  align-self: stretch;
  width: 100%;
  justify-content: space-between;
}

.manage-floor-plan {
  display: flex;
  padding: 0.25rem 0.75rem;
  align-items: center;
  gap: 0.4375rem;
  background-color: white;
  border-radius: 0.25rem;

  color: #0277bd;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
}

.manage-floor-plan:hover {
  cursor: pointer;
}

.controls-buttons {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.tickets-container {
  display: flex;
  width: 22.125rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;

  border-radius: 0.5rem 0 0 0.5rem;
  border: 2px solid #f5f5f5;
  background: #f5f5f5;
}

.available-stats-container {
  display: flex;
  padding: 1rem 1.5rem;
  align-items: flex-start;
  gap: 0.75rem;
  align-self: stretch;

  background-color: white;
}

.base-text {
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.bold-text {
  font-weight: 700;
}

.ticket-types-container {
  display: flex;
  padding: 1rem 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  align-self: stretch;

  background-color: white;
}

.circle {
  width: 1rem;
  height: 1rem;
  border-radius: 10000px;
}

.price-amount {
  color: #979797;
  leading-trim: both;
  text-edge: cap;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.additional-seat-info-container {
  display: flex;
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  align-self: stretch;

  background-color: white;
}

.additional-seat-info-title {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.03rem;
}

.additional-items {
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sold-circle {
  background: #f5f5f5;
  border: 1px solid #a1a0a0;
}

.booked-circle {
  background: #d9d9d9;
  border: 1px solid #979797;
  color: #333333;
  font-size: 10px;
  text-align: center;
}

.available-circle {
  background: #979797;
}

.unavailable-circle {
  background: #d9d9d9;
}

.selected-circle-available {
  background: #979797;
  border: 3px solid #333;
}

.selected-circle-unavailable {
  background: #d9d9d9;
  border: 3px solid #333;
}

.bkg-color-blue {
  background-color: #e6f1f8;
}

.managing-controls {
  display: flex;
  flex-direction: row-reverse;
  padding: 1rem 1.5rem;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
}

.reset-all-button {
  display: flex;
  height: 3rem;
  padding: 0.75rem 0.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;

  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0);
}

.reset-all-button:hover {
  cursor: pointer;
  background: white;
}

.reset-all-text {
  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'liga' off, 'clig' off;

  font-family: Raleway;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.01688rem;
  text-transform: uppercase;
}

.link-icon {
  display: flex;
  padding: 0.25rem;
  align-items: center;
  gap: -0.0625rem;

  border-radius: 1.5rem;
  background: #e6f1f8;
}

.tooltip {
  background-color: #333333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 0.125rem 0.625rem;
  position: absolute;
  z-index: 1;
  width: 80px;

  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333333 transparent transparent transparent;
}
</style>
